import {
  Container,
  Paper,
  Text,
  Title,
  Group,
  Button,
  TextInput,
  Select,
  Table,
  ActionIcon,
  Badge,
  Stack,
} from "@mantine/core";
import { IconPlus, IconSearch, IconEye } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { poApi } from "../../../api/po";
import dayjs from "dayjs";

const statusColors: Record<string, string> = {
  DRAFT: "gray",
  ISSUED: "blue",
  ACKNOWLEDGED: "green",
  CANCELLED: "red",
};

export function POList() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    search: "",
    status: "" as string,
  });

  const { data: pos, isLoading } = useQuery({
    queryKey: ["purchase-orders", filters],
    queryFn: () => poApi.list(filters.status ? { status: filters.status as any } : undefined),
  });

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  if (isLoading) {
    return (
      <Container size="xl" py="xl">
        <Text>Loading purchase orders...</Text>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Group justify="space-between" mb="xl">
        <Title order={2}>Purchase Orders</Title>
      </Group>

      <Paper shadow="sm" p="md" mb="xl">
        <Group gap="md">
          <TextInput
            placeholder="Search purchase orders..."
            leftSection={<IconSearch size={16} />}
            value={filters.search}
            onChange={(e) => handleFilterChange("search", e.target.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Status"
            data={[
              { value: "", label: "All Statuses" },
              { value: "DRAFT", label: "Draft" },
              { value: "ISSUED", label: "Issued" },
              { value: "ACKNOWLEDGED", label: "Acknowledged" },
              { value: "CANCELLED", label: "Cancelled" },
            ]}
            value={filters.status}
            onChange={(value) => handleFilterChange("status", value || "")}
            style={{ minWidth: 180 }}
          />
        </Group>
      </Paper>

      <Paper shadow="sm" p="md">
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>PO Number</Table.Th>
              <Table.Th>RFQ</Table.Th>
              <Table.Th>Vendor</Table.Th>
              <Table.Th>Total Amount</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Issued At</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {pos?.map((po) => (
              <Table.Tr key={po.id}>
                <Table.Td>{po.poNumber}</Table.Td>
                <Table.Td>
                  {po.rfq ? (
                    <Button
                      variant="subtle"
                      size="xs"
                      onClick={() => navigate(`/rfq/${po.rfqId}`)}
                    >
                      {po.rfq.rfqNumber}
                    </Button>
                  ) : (
                    <Button
                      variant="subtle"
                      size="xs"
                      onClick={() => navigate(`/rfq/${po.rfqId}`)}
                    >
                      View RFQ
                    </Button>
                  )}
                </Table.Td>
                <Table.Td>
                  {po.vendor.businessName || po.vendor.email}
                </Table.Td>
                <Table.Td>
                  {po.currency} {Number(po.totalAmount).toFixed(2)}
                </Table.Td>
                <Table.Td>
                  <Badge color={statusColors[po.status]} variant="light">
                    {po.status}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  {po.issuedAt
                    ? dayjs(po.issuedAt).format("DD MMM YYYY")
                    : "-"}
                </Table.Td>
                <Table.Td>
                  <ActionIcon
                    variant="subtle"
                    onClick={() => navigate(`/purchase-orders/${po.id}`)}
                  >
                    <IconEye size={16} />
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        {(!pos || pos.length === 0) && (
          <Stack align="center" py="xl">
            <Text c="dimmed">No purchase orders found</Text>
          </Stack>
        )}
      </Paper>
    </Container>
  );
}

